/* How Home describes what a user can reach, in one place so the header summary
   and the foot panel can never disagree. */

/**
 * The top-level modules the panel lists, in the order it lists them.
 *
 * `access.modules` is the app-wide code => granted map, twenty-three entries
 * deep including sub-modules and every `report.*` code. Listing it whole would
 * bury the seven answers a user actually wants under a wall of chips, so the
 * panel walks this list instead and ignores everything else in the map.
 *
 * The R.A.D.I.O. label is a Home-only override. ModuleType::label() answers
 * "General Risk" for the same code and is read by screens across the app;
 * changing it there to suit this panel would silently relabel all of them.
 */
export const TOP_LEVEL_MODULES = [
    { code: 'budgeting', label: () => $t('Budgeting') },
    { code: 'procurement', label: () => $t('Procurement') },
    { code: 'event_impact', label: () => $t('Event Impact') },
    { code: 'general_risk', label: () => $t('R.A.D.I.O.') },
    { code: 'project', label: () => $t('Project') },
    { code: 'external_erp', label: () => $t('External ERP') },
    { code: 'integrations', label: () => $t('Integrations') },
];

/* The modules whose central sections the sidebar hides from a
   department-scoped user, in the order the note names them. */
const SCOPED_MENU_MODULES = ['procurement', 'general_risk', 'project'];

/**
 * The note explaining why a department-scoped user holds a Procurement,
 * R.A.D.I.O. or Project module the sidebar does not show. It explains rather
 * than points elsewhere: the department pages gate these tabs separately, so
 * "find it there" could be untrue. Null when there is nothing to explain.
 */
export const scopedMenuNote = (access = {}) => {
    if (!access.department_scoped) {
        return null;
    }

    const modules = access.modules || {};
    const names = SCOPED_MENU_MODULES.filter((code) => !!modules[code]).map((code) => TOP_LEVEL_MODULES.find((module) => module.code === code).label());

    if (names.length === 0) {
        return null;
    }

    if (names.length === 1) {
        return $t("{{module}} isn't in your menu because your access is limited to selected departments.", { module: names[0] });
    }

    const list = new Intl.ListFormat(window.CURRENT_LOCALE || 'en', { type: 'conjunction' }).format(names);

    return $t("{{modules}} aren't in your menu because your access is limited to selected departments.", { modules: list });
};

/** How many names a chip list shows before it collapses the rest into "+N more". */
export const CHIP_LIMIT = 5;

/** The granted top-level modules as {id, name}, the shape the chip rows take. */
export const grantedModules = (modules = {}) =>
    TOP_LEVEL_MODULES.filter((module) => !!modules[module.code]).map((module) => ({ id: module.code, name: module.label() }));

/**
 * Whether the user can reach any report at all.
 *
 * The reports call to action is the one thing a user with no allocations can
 * still act on, so it is offered on the strength of a real `report.*` grant and
 * hidden outright otherwise — an empty Report Centre would be a worse landing
 * than no link at all.
 */
export const hasReportAccess = (modules = {}) => Object.keys(modules).some((code) => code.startsWith('report.') && !!modules[code]);

/**
 * The axes a user can hold something on, with how each one names itself.
 *
 * Revenue departments and cost centres each merge two tables — Revdepartment
 * with GeneralRevdepartment, Costcenter with Revcostcenter — because a user
 * reads them as one kind of thing and the split is an implementation detail of
 * which side of the budget a record sits on.
 */
const ACCESS_AXES = [
    {
        rows: (access) => access.departments || [],
        many: (count) => $t('{{count}} departments', { count }),
    },
    {
        rows: (access) => [...(access.cost_centres || []), ...(access.revenue_cost_centres || [])],
        many: (count) => $t('{{count}} cost centres', { count }),
    },
    {
        rows: (access) => [...(access.revenue_departments || []), ...(access.general_revenue_departments || [])],
        many: (count) => $t('{{count}} revenue departments', { count }),
    },
    {
        rows: (access) => access.venues || [],
        many: (count) => $t('{{count}} venues', { count }),
    },
];

/**
 * The one-line summary of what this user can reach.
 *
 * IT NAMES ONLY THE AXES THE USER ACTUALLY HOLDS SOMETHING ON. A fixed
 * three-part line — departments, cost centres, venues — reads as a complete
 * statement of entitlement, so for a Ticketing/Sponsorship user, who holds
 * revenue departments and nothing on the expenditure side at all, it would say
 * "0 departments · 0 cost centres · 0 venues" and tell exactly that user they
 * are entitled to nothing. An axis they hold nothing on is left out; the full
 * panel below is where "None" belongs, because there the label beside it says
 * which axis is empty.
 *
 * A user entitled to nothing anywhere gets an honest description rather than an
 * empty string, which would leave the line reading "Your access:" and nothing
 * after it.
 *
 * Exactly one record on an axis is NAMED rather than counted — "1 venues" is
 * both wrong and less useful than the venue's own name.
 */
export const accessSummary = (access = {}) => {
    if (access.full_access) {
        return $t('All departments, cost centres, revenue departments and venues');
    }

    const clauses = ACCESS_AXES.map((axis) => {
        const rows = axis.rows(access);

        if (rows.length === 0) {
            return null;
        }

        return rows.length === 1 ? rows[0].name : axis.many(rows.length);
    }).filter(Boolean);

    if (clauses.length === 0) {
        return $t('Read access — nothing assigned yet');
    }

    return clauses.join(' · ');
};
